import React from "react";
import {
  Control,
  Controller,
  FieldError,
  RegisterOptions,
} from "react-hook-form";

import { ICheckboxField, ICheckboxGroup } from "types";
import { FormLabel } from "components";

import { CheckboxField } from "./CheckboxField";
import { useExpandableOptions } from "../hooks";

interface CheckboxGroupProps extends ICheckboxGroup {
  options: ICheckboxField[];
  control: Control;
  validation?: RegisterOptions;
  value?: string[];
  error?: FieldError | null;
}

export const CheckboxGroup: React.FC<
  CheckboxGroupProps & React.InputHTMLAttributes<HTMLInputElement>
> = ({
  name,
  label,
  labelIcon,
  value,
  options,
  control,
  validation,
  required,
  disabled,
  error,
  onChange: triggerOnChange,
  expandable,
  expandableLimit,
  expandableMoreText,
  expandableLessText,
}) => {
  const { visibleOptions, renderExpandableButton } = useExpandableOptions({
    value,
    options,
    expandableOptions: {
      expandable,
      expandableLimit,
      expandableMoreText,
      expandableLessText,
    },
  });

  return (
    <Controller
      name={name}
      control={control}
      rules={validation}
      defaultValue={value}
      render={({ field: { onChange, value } }) => (
        <fieldset className="fieldset field field--checkbox-group">
          <legend className="fieldset">
            <FormLabel
              label={label}
              labelIcon={labelIcon}
              required={required}
            />
          </legend>
          {visibleOptions?.map((data) => (
            <CheckboxField
              {...data}
              key={`${name}_${data.value}`}
              id={`${name}_${data.value}`}
              name={name}
              checked={value?.includes(data.value)}
              disabled={disabled}
              onChange={(e) => {
                const { checked, value: checkboxValue } = e.target;
                const oldValue = value || [];
                let newValue;

                if (checked) {
                  newValue = [...oldValue, checkboxValue];
                } else {
                  newValue = oldValue.filter(
                    (id: string) => id !== checkboxValue
                  );
                }

                triggerOnChange && triggerOnChange(newValue);

                onChange(newValue?.length === 0 ? "" : newValue);
              }}
            />
          ))}

          {renderExpandableButton()}

          {error?.message && (
            <span className="form-message is-error is-animated" role="alert">
              {error?.message}
            </span>
          )}
        </fieldset>
      )}
    />
  );
};
